import { parseTargetKey } from "@playbookai/widget-schemas";
import {
  proposalFieldLabel,
  proposalRowLabel,
} from "../shell/proposal-diff.js";

/**
 * PB-4923 AC6 — turn a canonical target key into something a person can read.
 *
 * The executor reports a skip as `branches[br-1]/causes[c-2]/text`, which is
 * exactly right for a revision map and useless in a sentence. AC6 asks the
 * product to say WHICH FIELD, so this resolves the key against the widget's own
 * CONTENT — the params the command response already returns — rather than
 * against a per-kind label table.
 *
 * Content, not a table, for three reasons:
 *
 * - A table would need an entry per field per kind, 28 kinds deep, and would
 *   silently go stale the first time a schema gained a field: a missing entry
 *   reads as "no label", which looks like working.
 * - The useful half of the name is the item's OWN text ("the 'Machine
 *   calibration' cause"), and only the content has that. A table could only
 *   ever say "a cause".
 * - The client already holds the content. `WidgetCommandResult.params` is the
 *   new authoritative params, so nothing has to be fetched to name a skip.
 *
 * Returns `null` when the key cannot be resolved — an unparseable key, or an
 * item the params no longer contain because it was deleted. A caller must then
 * fall back to copy that names no field, never to the raw key.
 */
export function widgetTargetLabel(
  targetKey: string,
  params: unknown,
): string | null {
  const path = parseTargetKey(targetKey);
  if (path === null || path.length === 0) return null;

  let cursor: unknown = params;
  let itemLabel: string | null = null;
  let leaf: string | null = null;

  for (const segment of path) {
    switch (segment.kind) {
      case "prop":
        leaf = segment.name;
        cursor = record(cursor)?.[segment.name];
        break;
      case "collection":
        leaf = segment.name;
        cursor = record(cursor)?.[segment.name];
        break;
      case "item": {
        const list = record(cursor)?.[segment.collection];
        if (!Array.isArray(list)) return null;
        const index = list.findIndex(
          (entry) => record(entry)?.["id"] === segment.itemId,
        );
        if (index === -1) return null;
        cursor = list[index];
        itemLabel = proposalRowLabel(cursor, index);
        leaf = null;
        break;
      }
      case "entry": {
        // A table cell. Its own value is not a name, so the COLUMN names it —
        // the record key is a column id, and `genericTable` carries the human
        // label on the column itself.
        leaf = columnLabel(params, segment.key) ?? segment.key;
        cursor = record(cursor)?.[segment.record];
        break;
      }
    }
  }

  if (leaf !== null && itemLabel !== null) {
    return `${proposalFieldLabel(leaf).toLowerCase()} of “${itemLabel}”`;
  }
  if (itemLabel !== null) return `“${itemLabel}”`;
  if (leaf !== null) return `the ${proposalFieldLabel(leaf).toLowerCase()}`;
  return null;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** `genericTable` addresses a cell by column id; the column holds the label. */
function columnLabel(params: unknown, columnId: string): string | null {
  const columns = record(params)?.["columns"];
  if (!Array.isArray(columns)) return null;
  const column = columns.find((entry) => record(entry)?.["id"] === columnId);
  const label = record(column)?.["label"];
  return typeof label === "string" && label.trim() ? label : null;
}
